<template>
	<div class="content">
		<base-header class="pb-6">
			<div class="row align-items-center py-4">
				<div class="col-lg-8 col-7">
					<h6 class="h2 text-white d-inline-block mb-0">{{ isNew ? $t("pos.event.title_new") : $t("pos.event.title_edit") }}</h6>
					<nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
						<route-bread-crumb></route-bread-crumb>
					</nav>
				</div>
				<div class="col-lg-4 col-5 text-right">
					<base-button v-if="!isNew" size="md" type="danger" :disabled="form.bill_count > 0" @click="remove">{{ $t("pos.common.delete") }}</base-button>
				</div>
			</div>
		</base-header>

		<div class="container-fluid mt--6">
			<pos-install-alert :show="notInstalled"></pos-install-alert>
			<!-- products table edge-to-edge in the card (as /backoffice/orders-overview): card body px-0,
			     everything else in px-4; pos-tabs-flush pads the tab header -->
			<card v-loading="loading" body-classes="px-0">
				<div class="px-4">
					<div class="row">
						<div class="col-md-6">
							<base-input :label="$t('pos.event.name') + ' *'" v-model="form.name" maxlength="150"></base-input>
						</div>
						<div class="col-md-6">
							<base-input :label="$t('pos.event.location')" v-model="form.location" maxlength="255"></base-input>
						</div>
						<div class="col-md-3 col-6 form-group">
							<label class="form-control-label d-block">{{ $t("pos.event.start_date") }} *</label>
							<el-date-picker v-model="form.start_date" type="date" value-format="yyyy-MM-dd" format="dd/MM/yyyy" class="w-100"></el-date-picker>
						</div>
						<div class="col-md-3 col-6 form-group">
							<label class="form-control-label d-block">{{ $t("pos.event.end_date") }} *</label>
							<el-date-picker v-model="form.end_date" type="date" value-format="yyyy-MM-dd" format="dd/MM/yyyy" class="w-100"></el-date-picker>
							<small v-if="dateError" class="text-danger">{{ $t("pos.event.end_before_start") }}</small>
						</div>
						<div class="col-md-6 form-group">
							<label class="form-control-label d-block">{{ $t("pos.event.status") }}</label>
							<el-radio-group v-model="form.status">
								<el-radio :label="1">{{ $t("pos.common.active") }}</el-radio>
								<el-radio :label="0">{{ $t("pos.common.inactive") }}</el-radio>
							</el-radio-group>
						</div>
						<div class="col-md-3 col-6 form-group">
							<label class="form-control-label d-block">{{ $t("pos.event.cap_pct") }}</label>
							<el-input-number v-model="form.discount_cap_pct" :min="0" :max="100" :precision="2" :controls="false" class="w-100"></el-input-number>
						</div>
						<div class="col-md-3 col-6 form-group">
							<label class="form-control-label d-block">{{ $t("pos.event.cap_amount") }}</label>
							<el-input-number v-model="form.discount_cap_amount" :min="0" :precision="2" :controls="false" class="w-100"></el-input-number>
						</div>
						<div class="col-12 mt--2 mb-3"><small class="text-muted">{{ $t("pos.event.cap_hint") }}</small></div>
						<div class="col-md-6 form-group">
							<label class="form-control-label d-block">{{ $t("pos.event.devices") }}</label>
							<el-select v-model="form.device_ids" multiple filterable class="w-100">
								<el-option v-for="d in devices" :key="d.id" :label="d.code + ' ' + d.name" :value="d.id"></el-option>
							</el-select>
						</div>
						<div class="col-md-6 form-group">
							<label class="form-control-label d-block">{{ $t("pos.event.note") }}</label>
							<el-input type="textarea" :rows="2" v-model="form.note" maxlength="500"></el-input>
						</div>
						<!-- phase 3: Top Spender board -->
						<div class="col-12">
							<div class="pos-board-card mb-4">
								<h5 class="mb-2">{{ $t("pos.event.board_card") }}</h5>
								<div class="d-flex align-items-center mb-2">
									<el-switch v-model="form.board_enabled" class="mr-2"></el-switch>
									<span>{{ $t("pos.event.board_enabled") }}</span>
								</div>
								<div v-if="form.board_url" class="d-flex align-items-center flex-wrap">
									<el-input :value="form.board_url" readonly size="small" class="pos-board-url mr-2 mb-1"></el-input>
									<base-button size="sm" type="secondary" class="mb-1" @click="copyBoard">{{ $t("pos.event.board_copy") }}</base-button>
									<a :href="form.board_url" target="_blank" rel="noopener" class="btn btn-sm btn-link mb-1">{{ $t("pos.event.board_open") }}</a>
								</div>
								<small v-else class="text-muted d-block">{{ $t("pos.event.board_no_url") }}</small>
								<small class="text-muted d-block mt-1">{{ $t("pos.event.board_hint") }}</small>
							</div>
						</div>
						<!-- §13.2: staff of this event (the app's staff picker shows only them) -->
						<div class="col-12">
							<div class="pos-board-card mb-4 pos-event-staff">
								<h5 class="mb-2">{{ $t("pos.event.staff_title") }}</h5>
								<small v-if="isNew" class="text-muted d-block">{{ $t("pos.event.staff_after_save") }}</small>
								<!-- #448 minor 2: the install hint only when the server says installed:false;
								     a network / 403 error is an error with retry, not "not installed" -->
								<div v-else-if="staff.error" class="pos-event-staff-error">
									<el-alert type="error" :title="staff.error" :closable="false" show-icon></el-alert>
									<base-button size="sm" type="secondary" class="mt-2" :disabled="staff.loading" @click="loadStaff">{{ $t("pos.event.staff_retry") }}</base-button>
								</div>
								<el-alert v-else-if="!staff.installed" type="warning" :title="$t('pos.event.staff_not_installed')" :closable="false" show-icon></el-alert>
								<template v-else>
									<el-select v-model="staff.ids" multiple filterable class="w-100" :placeholder="$t('pos.event.staff_placeholder')">
										<el-option v-for="u in staff.candidates" :key="u.user_id" :label="u.display_name" :value="u.user_id"></el-option>
									</el-select>
									<small class="text-muted d-block mt-1">{{ $t("pos.event.staff_hint") }}</small>
								</template>
							</div>
						</div>
					</div>
				</div>

				<el-tabs v-model="tab" class="pos-tabs-flush">
					<el-tab-pane :label="$t('pos.event.tab_products')" name="products">
						<div class="px-4">
							<pos-product-picker :exclude="form.products.map((p) => p.pos_product_id)" class="mb-2" @pick="addProduct"></pos-product-picker>
						</div>
						<el-table :data="form.products" size="small" header-row-class-name="thead-light" class="bon-table pos-table">
							<el-table-column type="index" width="40"></el-table-column>
							<el-table-column :label="$t('pos.event.image')" width="70" align="center">
								<template slot-scope="{ row }">
									<img v-if="row.image" :src="imgUrl(row.image)" class="pos-thumb-sm" alt="" />
								</template>
							</el-table-column>
							<el-table-column :label="$t('pos.event.product')" min-width="220">
								<template slot-scope="{ row }">
									{{ row.name }}
									<el-tag v-if="row.status === 0" type="info" size="mini">{{ $t("pos.product.inactive_tag") }}</el-tag>
								</template>
							</el-table-column>
							<el-table-column :label="$t('pos.event.code')" prop="code" width="110"></el-table-column>
							<el-table-column :label="$t('pos.event.retail_price')" width="110" align="right">
								<template slot-scope="{ row }">{{ money(row.price) }}</template>
							</el-table-column>
							<el-table-column :label="$t('pos.event.special_price')" width="200">
								<template slot-scope="{ row }">
									<el-input-number v-model="row.special_price" :min="0" :precision="2" :controls="false" size="small" class="w-100"></el-input-number>
								</template>
							</el-table-column>
							<!-- row actions pinned at the right edge (as the ⋮ column of orders-overview) -->
							<el-table-column :label="$t('actions')" width="132" fixed="right" align="center" class-name="bon-tight">
								<template slot-scope="{ $index }">
									<div class="pos-row-actions">
										<base-button size="sm" type="secondary" icon :disabled="$index === 0" :aria-label="$t('pos.common.move_up')" :title="$t('pos.common.move_up')" @click="move($index, -1)"><i class="fa fa-arrow-up text-info"></i></base-button>
										<base-button size="sm" type="secondary" icon :disabled="$index === form.products.length - 1" :aria-label="$t('pos.common.move_down')" :title="$t('pos.common.move_down')" @click="move($index, 1)"><i class="fa fa-arrow-down text-info"></i></base-button>
										<base-button size="sm" type="secondary" icon :aria-label="$t('pos.common.remove')" :title="$t('pos.common.remove')" @click="form.products.splice($index, 1)"><i class="fa fa-trash text-danger"></i></base-button>
									</div>
								</template>
							</el-table-column>
						</el-table>
					</el-tab-pane>
					<el-tab-pane :label="$t('pos.event.tab_receipt')" name="receipt" class="px-4">
						<div class="d-flex align-items-center mb-3">
							<el-switch v-model="receiptOn" class="mr-2"></el-switch>
							<span class="mr-4">{{ $t("pos.event.receipt_override") }}</span>
							<base-button v-if="receiptOn" size="sm" type="secondary" @click="copyDefault">{{ $t("pos.event.copy_default") }}</base-button>
						</div>
						<pos-receipt-form v-if="receiptOn" v-model="receipt"></pos-receipt-form>
					</el-tab-pane>
					<!-- phase 3 R-PAY: per-event receiving account (B49) -->
					<el-tab-pane :label="$t('pos.event.tab_payment')" name="payment" class="px-4">
						<div class="d-flex align-items-center mb-3">
							<el-switch v-model="accountOn" class="mr-2"></el-switch>
							<span class="mr-4">{{ $t("pos.event.payment_override") }}</span>
							<base-button v-if="accountOn" size="sm" type="secondary" @click="copyAccount">{{ $t("pos.event.copy_default") }}</base-button>
						</div>
						<pos-payment-account-form v-if="accountOn" v-model="account" :bad="accountBad"></pos-payment-account-form>
					</el-tab-pane>
				</el-tabs>

				<div class="text-right mt-4 px-4">
					<base-button type="secondary" @click="$router.push({ name: 'PosEvents' })">{{ $t("pos.common.cancel") }}</base-button>
					<base-button type="primary" :disabled="saving || !valid" @click="save">{{ $t("pos.common.save") }}</base-button>
				</div>
			</card>
		</div>
	</div>
</template>

<script>
import { Table, TableColumn, Tag, Select, Option, DatePicker, RadioGroup, Radio, InputNumber, Tabs, TabPane, Switch, Alert } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { PosService } from "@/resource";
import { posMoney, posProductImageUrl, posErrorMessage } from "@/utils/pos";
import posPageMixin from "./posPageMixin";
import PosInstallAlert from "./PosInstallAlert.vue";
import PosProductPicker from "./PosProductPicker.vue";
import PosReceiptForm from "./PosReceiptForm.vue";
import PosPaymentAccountForm from "./PosPaymentAccountForm.vue";
import { paymentAccountForm, paymentAccountBody, validatePaymentAccount } from "./posPaymentAccount";

const emptyForm = () => ({
	name: "",
	location: "",
	start_date: "",
	end_date: "",
	status: 1,
	discount_cap_pct: undefined,
	discount_cap_amount: undefined,
	note: "",
	device_ids: [],
	products: [],
	bill_count: 0,
	board_enabled: true,
	board_url: ""
});
const receiptFields = (r) => ({
	logo_data_url: (r && r.logo_data_url) || "",
	shop_name: (r && r.shop_name) || "",
	address: (r && r.address) || "",
	phone: (r && r.phone) || "",
	header_note: (r && r.header_note) || "",
	footer_text: (r && r.footer_text) || "",
	footer_qr_text: (r && r.footer_qr_text) || "",
	footer_qr_caption: (r && r.footer_qr_caption) || "",
	show_customer_address: !r || r.show_customer_address !== false
});
const numOrNull = (v) => (v === undefined || v === null || v === "" || isNaN(Number(v)) ? null : Number(v));

export default {
	name: "PosEventForm",
	mixins: [posPageMixin],
	components: {
		RouteBreadCrumb,
		PosInstallAlert,
		PosProductPicker,
		PosReceiptForm,
		PosPaymentAccountForm,
		[Table.name]: Table,
		[TableColumn.name]: TableColumn,
		[Tag.name]: Tag,
		[Select.name]: Select,
		[Option.name]: Option,
		[DatePicker.name]: DatePicker,
		[RadioGroup.name]: RadioGroup,
		[Radio.name]: Radio,
		[InputNumber.name]: InputNumber,
		[Tabs.name]: Tabs,
		[TabPane.name]: TabPane,
		[Switch.name]: Switch,
		[Alert.name]: Alert
	},
	data() {
		return {
			form: emptyForm(),
			tab: "products",
			receiptOn: false,
			receipt: receiptFields(null),
			hadReceipt: false,
			accountOn: false,
			account: paymentAccountForm(null),
			accountHasQr: false,
			accountBad: [],
			hadAccount: false,
			staff: { ids: [], candidates: [], installed: true, loaded: false, error: "", loading: false },
			saving: false,
			apiBase: process.env.VUE_APP_API_BASE_URL || ""
		};
	},
	computed: {
		isNew() {
			return this.$route.params.id === "new";
		},
		devices() {
			return this.$store.getters["Pos/devices"] || [];
		},
		dateError() {
			return !!(this.form.start_date && this.form.end_date && this.form.end_date < this.form.start_date);
		},
		valid() {
			return this.form.name.trim() !== "" && !!this.form.start_date && !!this.form.end_date && !this.dateError;
		}
	},
	watch: {
		// Kept alive in the taskbar: leaving this page also changes
		// $route.params.id (to undefined). Only reload while this page is the
		// active route, or it fetches "/undefined" and pops a 404 swal over
		// whatever page the user moved to.
		"$route.params.id"(val) {
			if (this.$route.name !== "PosEventForm" || val === undefined) return;
			this.load();
		}
	},
	mounted() {
		this.load();
	},
	methods: {
		// §13.2 event staff. Never blocks the event form (fail-soft); the install
		// hint is shown only for the server's installed:false (#448 minor 2).
		async loadStaff() {
			this.staff = { ...this.staff, loading: true, error: "" };
			try {
				const st = await PosService.eventStaffGet(this.$route.params.id);
				const sd = (st.data && st.data.Datum) || {};
				this.staff = { ids: sd.user_ids || [], candidates: sd.candidates || [], installed: sd.installed !== false, loaded: true, error: "", loading: false };
			} catch (e) {
				// event staff is optional: show an error with retry, keep the rest of the form usable
				const msg = posErrorMessage(e, (k) => this.$t(k), (k) => this.$te(k));
				this.staff = { ids: [], candidates: [], installed: true, loaded: false, error: this.$t("pos.event.staff_load_error") + (msg ? " — " + msg : ""), loading: false };
			}
		},
		money: posMoney,
		imgUrl(name) {
			return posProductImageUrl(this.apiBase, name);
		},
		async load() {
			this.$store.dispatch("Pos/fetchLookups", true).catch(() => {});
			this.loading = true;
			try {
				this.accountBad = [];
				if (this.isNew) {
					this.form = emptyForm();
					this.receiptOn = false;
					this.hadReceipt = false;
					this.receipt = receiptFields(null);
					this.accountOn = false;
					this.hadAccount = false;
					this.accountHasQr = false;
					this.account = paymentAccountForm(null);
				} else {
					const res = await PosService.PosEventGet(this.$route.params.id);
					const d = (res.data && res.data.Datum) || {};
					this.form = {
						name: d.name || "",
						location: d.location || "",
						start_date: d.start_date || "",
						end_date: d.end_date || "",
						status: d.status === 0 ? 0 : 1,
						discount_cap_pct: d.discount_cap_pct === null ? undefined : d.discount_cap_pct,
						discount_cap_amount: d.discount_cap_amount === null ? undefined : d.discount_cap_amount,
						note: d.note || "",
						device_ids: d.device_ids || [],
						products: (d.products || []).map((p) => ({ ...p, special_price: p.special_price === null ? undefined : p.special_price })),
						bill_count: d.bill_count || 0,
						board_enabled: d.board_enabled !== false,
						board_url: d.board_url || ""
					};
					await this.loadStaff();
					const pa = await PosService.eventPaymentAccountGet(this.$route.params.id);
					const pad = (pa.data && pa.data.Datum) || null;
					const hasRow = !!(pad && pad.mode);
					this.hadAccount = hasRow;
					this.accountOn = hasRow && pad.enabled !== false;
					this.accountHasQr = !!(pad && pad.qr_image_data_url);
					this.account = paymentAccountForm(hasRow ? pad : null);
					const ro = d.receipt_override;
					this.hadReceipt = !!ro;
					this.receiptOn = !!(ro && ro.enabled !== false);
					this.receipt = receiptFields(ro);
				}
				this.notInstalled = false;
			} catch (err) {
				this.posError(err);
			} finally {
				this.loading = false;
			}
		},
		addProduct(p) {
			if (this.form.products.some((x) => x.pos_product_id === p.id)) return;
			this.form.products.push({ pos_product_id: p.id, name: p.name, code: p.code, image: p.image, price: p.price, special_price: undefined, status: p.status });
		},
		move(i, dir) {
			const list = this.form.products;
			const j = i + dir;
			if (j < 0 || j >= list.length) return;
			const tmp = list[i];
			this.$set(list, i, list[j]);
			this.$set(list, j, tmp);
		},
		async copyBoard() {
			try {
				await navigator.clipboard.writeText(this.form.board_url);
				this.posToast("pos.event.board_copied");
			} catch (e) {
				window.prompt(this.$t("pos.event.board_url"), this.form.board_url);
			}
		},
		async copyAccount() {
			try {
				const res = await PosService.paymentAccountGet();
				const d = (res.data && res.data.Datum) || null;
				this.account = { ...paymentAccountForm(d && d.mode ? d : null), enabled: true };
				this.accountHasQr = !!(d && d.qr_image_data_url);
			} catch (err) {
				this.posError(err);
			}
		},
		async copyDefault() {
			try {
				const res = await PosService.PosReceiptSettingGet();
				this.receipt = receiptFields((res.data && res.data.Datum) || null);
			} catch (err) {
				this.posError(err);
			}
		},
		// Save order (phase 3 §6): B13/B15 → B17 → B18 → B20 → B49; the first
		// failure stops and shows its error.
		async save() {
			if (!this.valid) return;
			if (this.accountOn) {
				this.accountBad = validatePaymentAccount(this.account, this.accountHasQr);
				if (this.accountBad.length) {
					this.tab = "payment";
					return;
				}
			}
			this.saving = true;
			const f = this.form;
			const body = {
				name: f.name.trim(),
				location: f.location,
				start_date: f.start_date,
				end_date: f.end_date,
				status: f.status,
				discount_cap_pct: numOrNull(f.discount_cap_pct),
				discount_cap_amount: numOrNull(f.discount_cap_amount),
				note: f.note,
				board_enabled: !!f.board_enabled
			};
			try {
				let id = this.$route.params.id;
				if (this.isNew) {
					const res = await PosService.PosEventCreate(body);
					id = ((res.data && res.data.Datum) || {}).id;
				} else {
					await PosService.PosEventUpdate(id, body);
				}
				await PosService.PosEventDevicesPut(id, { device_ids: f.device_ids });
				await PosService.PosEventProductsPut(id, {
					items: f.products.map((p) => ({ pos_product_id: p.pos_product_id, special_price: numOrNull(p.special_price) }))
				});
				if (this.receiptOn) await PosService.PosEventReceiptPut(id, { ...this.receipt, enabled: true });
				else if (this.hadReceipt) await PosService.PosEventReceiptDelete(id);
				if (this.accountOn) await PosService.eventPaymentAccountPut(id, paymentAccountBody({ ...this.account, enabled: true }, true));
				else if (this.hadAccount) await PosService.eventPaymentAccountDelete(id);
				// §13.2 last: event staff (only when the 3c table is installed)
				if (!this.isNew && this.staff.loaded && this.staff.installed) await PosService.eventStaffPut(id, { user_ids: this.staff.ids });
				this.posToast("pos.common.saved");
				if (this.isNew) this.$router.replace({ name: "PosEventForm", params: { id: String(id) } });
				else this.load();
			} catch (err) {
				const r = err && err.response;
				if (r && r.data && r.data.Msg === "pos_payment_account_invalid") {
					this.accountBad = String(r.data.Cause || "").split(",");
					this.tab = "payment";
				}
				if (r && r.data && r.data.Msg === "pos_qr_image_invalid") {
					this.accountBad = ["qr_image_data_url"];
					this.tab = "payment";
				}
				this.posError(err);
			} finally {
				this.saving = false;
			}
		},
		async remove() {
			if (!(await this.posConfirm(this.$t("pos.event.confirm_delete")))) return;
			try {
				await PosService.PosEventDelete(this.$route.params.id);
				this.posToast("pos.common.deleted");
				this.$router.push({ name: "PosEvents" });
			} catch (err) {
				this.posError(err);
			}
		}
	}
};
</script>

<style>
.pos-board-card {
	border: 1px solid #e9ecef;
	border-radius: 8px;
	padding: 12px 16px;
	background: #f8f9fe;
}
.pos-board-url {
	max-width: 520px;
}
.pos-thumb-sm {
	width: 36px;
	height: 36px;
	object-fit: cover;
	border-radius: 4px;
}
</style>
